import { createRng } from '@battle/sim';
import { SkyMesh } from 'three/addons/objects/SkyMesh.js';
import {
  color,
  cos,
  float,
  hash,
  instanceIndex,
  mix,
  positionLocal,
  positionWorld,
  sin,
  texture,
  time,
  vec3,
} from 'three/tsl';
import * as THREE from 'three/webgpu';
import type { QualitySettings } from '../render/quality.ts';
import { createArenaFloorTextures } from './arena-floor.ts';
import { createGrass } from './grass.ts';
import { createNoise2D } from './noise.ts';

/** Arena layout (meters). See the Sunken Grove concept art A-B in art/concepts. */
export const ARENA = {
  floorRadius: 22,
  pillarRadius: 25,
  pool: { x: -13.5, z: 7, radius: 5.5 },
} as const;

const SUN = { elevationDeg: 7, azimuthDeg: 150 };

export interface World {
  sun: THREE.DirectionalLight;
  update(dt: number, elapsed: number): void;
}

/** Look-dev switches, e.g. ?noenv&nofog. */
export interface WorldDebug {
  noEnv?: boolean;
  noFog?: boolean;
}

export function buildSunkenGrove(
  scene: THREE.Scene,
  renderer: THREE.WebGPURenderer,
  quality: QualitySettings,
  debug: WorldDebug = {},
): World {
  const rng = createRng(1337);
  const noise = createNoise2D(99);

  // ---------- Sky, sun, atmosphere ----------
  const sunDir = new THREE.Vector3().setFromSphericalCoords(
    1,
    THREE.MathUtils.degToRad(90 - SUN.elevationDeg),
    THREE.MathUtils.degToRad(SUN.azimuthDeg),
  );
  const configureSky = (sky: SkyMesh) => {
    sky.turbidity.value = 9;
    sky.rayleigh.value = 1.5;
    sky.mieCoefficient.value = 0.008;
    sky.mieDirectionalG.value = 0.82;
    sky.cloudCoverage.value = 0.35;
    sky.cloudDensity.value = 0.35;
    sky.cloudElevation.value = 0.55;
    sky.sunPosition.value.copy(sunDir);
  };
  const sky = new SkyMesh();
  sky.scale.setScalar(450000);
  configureSky(sky);
  scene.add(sky);

  // Image-based lighting from the same sky (captured once).
  if (!debug.noEnv)
    try {
      const envScene = new THREE.Scene();
      const envSky = new SkyMesh();
      envSky.scale.setScalar(90);
      configureSky(envSky);
      envScene.add(envSky);
      const pmrem = new THREE.PMREMGenerator(renderer);
      scene.environment = pmrem.fromScene(envScene, 0.02, 0.1, 100).texture;
      scene.environmentIntensity = 0.3;
      pmrem.dispose();
    } catch (err) {
      console.warn('environment capture failed; using hemisphere light only', err);
    }

  if (!debug.noFog) scene.fog = new THREE.FogExp2(0xb48d62, 0.0075);

  const hemi = new THREE.HemisphereLight(0xbcd2ff, 0x4a3b22, 0.6);
  scene.add(hemi);

  const sun = new THREE.DirectionalLight(0xffc98a, 2.6);
  sun.position.copy(sunDir).multiplyScalar(60);
  sun.castShadow = true;
  sun.shadow.mapSize.setScalar(quality.shadowMapSize);
  const sc = sun.shadow.camera;
  sc.left = -34;
  sc.right = 34;
  sc.top = 34;
  sc.bottom = -34;
  sc.near = 1;
  sc.far = 160;
  sun.shadow.bias = -0.0004;
  sun.shadow.normalBias = 0.04;
  scene.add(sun, sun.target);

  // Cool fill from the side opposite the sun keeps shadowed sides readable.
  const fill = new THREE.DirectionalLight(0x9db8ff, 0.7);
  fill.position.set(-sunDir.x, 0.6, -sunDir.z).multiplyScalar(40);
  scene.add(fill);

  // ---------- Terrain: flat arena bowl, rolling hills beyond ----------
  const heightAt = (x: number, z: number): number => {
    const r = Math.hypot(x, z);
    const rise = THREE.MathUtils.smoothstep(r, 30, 80);
    // Keep the ground just under the arena floor, then let it roll outside the rim.
    const bumps = THREE.MathUtils.smoothstep(r, ARENA.floorRadius + 1, ARENA.floorRadius + 8);
    return (
      rise * (4 + noise(x * 0.02, z * 0.02) * 16) +
      (noise(x * 0.1, z * 0.1) - 0.5) * 0.6 * bumps -
      0.12
    );
  };
  const terrainGeo = new THREE.PlaneGeometry(260, 260, 180, 180);
  terrainGeo.rotateX(-Math.PI / 2);
  const tp = terrainGeo.attributes.position as THREE.BufferAttribute;
  const colors = new Float32Array(tp.count * 3);
  const grassLow = new THREE.Color('#4d5e26');
  const grassHigh = new THREE.Color('#9a8b43');
  const dirt = new THREE.Color('#6b5236');
  const c = new THREE.Color();
  for (let i = 0; i < tp.count; i++) {
    const x = tp.getX(i);
    const z = tp.getZ(i);
    const y = heightAt(x, z);
    tp.setY(i, y);
    const n = noise(x * 0.06 + 10, z * 0.06 - 4);
    c.copy(grassLow).lerp(grassHigh, n);
    const r = Math.hypot(x, z);
    if (r < ARENA.floorRadius + 3)
      c.lerp(dirt, 1 - THREE.MathUtils.smoothstep(r, ARENA.floorRadius, ARENA.floorRadius + 3));
    colors.set([c.r, c.g, c.b], i * 3);
  }
  terrainGeo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  terrainGeo.computeVertexNormals();
  const terrain = new THREE.Mesh(
    terrainGeo,
    new THREE.MeshStandardNodeMaterial({ vertexColors: true, roughness: 0.95 }),
  );
  terrain.receiveShadow = true;
  scene.add(terrain);

  // ---------- Arena floor with glowing glyph ring ----------
  const { map, emissiveMap } = createArenaFloorTextures(quality.tier === 'low' ? 1024 : 2048);
  const floorMat = new THREE.MeshStandardNodeMaterial({ map, roughness: 0.82 });
  // Glyph grooves glow softly and breathe.
  const glyphPulse = sin(time.mul(0.8)).mul(0.25).add(0.75);
  floorMat.emissiveNode = texture(emissiveMap).rgb.mul(color('#ffb347')).mul(glyphPulse.mul(2.2));
  const floor = new THREE.Mesh(new THREE.CircleGeometry(ARENA.floorRadius, 128), floorMat);
  floor.rotation.x = -Math.PI / 2;
  floor.position.y = 0.03;
  floor.receiveShadow = true;
  scene.add(floor);

  // Raised stone rim around the floor.
  const rim = new THREE.Mesh(
    new THREE.TorusGeometry(ARENA.floorRadius + 0.15, 0.35, 8, 160),
    new THREE.MeshStandardNodeMaterial({ color: '#7d725f', roughness: 0.9 }),
  );
  rim.rotation.x = Math.PI / 2;
  rim.position.y = 0.05;
  rim.castShadow = rim.receiveShadow = true;
  scene.add(rim);

  // ---------- Ruined pillars ----------
  const stoneMat = new THREE.MeshStandardNodeMaterial({ vertexColors: true, roughness: 0.88 });
  const mossy = (geo: THREE.BufferGeometry, top: number) => {
    const pos = geo.attributes.position as THREE.BufferAttribute;
    const col = new Float32Array(pos.count * 3);
    const stone = new THREE.Color('#9b907c');
    const moss = new THREE.Color('#58702c');
    for (let i = 0; i < pos.count; i++) {
      const t = THREE.MathUtils.clamp((pos.getY(i) + top * 0.5) / top, 0, 1);
      const n = noise(pos.getX(i) * 3 + i * 0.001, pos.getY(i) * 2);
      c.copy(stone)
        .multiplyScalar(0.85 + n * 0.25)
        .lerp(moss, THREE.MathUtils.smoothstep(t + n * 0.3, 0.75, 1.1));
      col.set([c.r, c.g, c.b], i * 3);
    }
    geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    return geo;
  };
  const pillarCount = 12;
  for (let i = 0; i < pillarCount; i++) {
    const a = (i / pillarCount) * Math.PI * 2 + 0.13;
    const broken = rng.next() < 0.45;
    const height = broken ? 1.6 + rng.next() * 2.4 : 5.5 + rng.next() * 2;
    const geo = mossy(new THREE.CylinderGeometry(0.85, 1.0, height, 14, 6), height);
    const pillar = new THREE.Mesh(geo, stoneMat);
    const x = Math.cos(a) * ARENA.pillarRadius;
    const z = Math.sin(a) * ARENA.pillarRadius;
    pillar.position.set(x, heightAt(x, z) + height / 2 - 0.1, z);
    pillar.rotation.set((rng.next() - 0.5) * 0.08, rng.next() * Math.PI, (rng.next() - 0.5) * 0.08);
    pillar.castShadow = pillar.receiveShadow = true;
    scene.add(pillar);
    if (!broken) {
      const cap = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.5, 2.4), stoneMat);
      mossy(cap.geometry, 0.5);
      cap.position.set(x, pillar.position.y + height / 2 + 0.2, z);
      cap.rotation.y = a;
      cap.castShadow = cap.receiveShadow = true;
      scene.add(cap);
    } else {
      const block = new THREE.Mesh(mossy(new THREE.BoxGeometry(1.6, 0.9, 1.1), 0.9), stoneMat);
      block.position.set(x * 1.08 + rng.next(), 0.35, z * 1.08 + rng.next());
      block.rotation.set(rng.next() * 0.4, rng.next() * Math.PI, rng.next() * 0.3);
      block.castShadow = block.receiveShadow = true;
      scene.add(block);
    }
  }

  // ---------- Pool ----------
  const { pool } = ARENA;
  const waterMat = new THREE.MeshStandardNodeMaterial({
    transparent: true,
    roughness: 0.06,
    metalness: 0.0,
  });
  const ripple = sin(positionWorld.x.mul(2.1).add(time.mul(1.3)))
    .mul(cos(positionWorld.z.mul(1.7).sub(time.mul(1.1))))
    .mul(0.06);
  waterMat.colorNode = mix(color('#1d5d63'), color('#3fb6b0'), ripple.add(0.5));
  waterMat.opacityNode = float(0.86);
  const water = new THREE.Mesh(new THREE.CircleGeometry(pool.radius, 64), waterMat);
  water.rotation.x = -Math.PI / 2;
  water.position.set(pool.x, 0.1, pool.z);
  water.receiveShadow = true;
  scene.add(water);
  const poolRim = new THREE.Mesh(
    new THREE.TorusGeometry(pool.radius + 0.1, 0.3, 8, 96),
    new THREE.MeshStandardNodeMaterial({ color: '#827763', roughness: 0.9 }),
  );
  poolRim.rotation.x = Math.PI / 2;
  poolRim.position.set(pool.x, 0.1, pool.z);
  poolRim.castShadow = poolRim.receiveShadow = true;
  scene.add(poolRim);

  // ---------- Rocks ----------
  const rockMat = new THREE.MeshStandardNodeMaterial({
    color: '#8a806c',
    roughness: 0.92,
    flatShading: true,
  });
  for (let i = 0; i < 26; i++) {
    const geo = new THREE.IcosahedronGeometry(1, 1);
    const pos = geo.attributes.position as THREE.BufferAttribute;
    for (let v = 0; v < pos.count; v++) {
      const k = 0.75 + noise(pos.getX(v) * 1.7 + i, pos.getZ(v) * 1.7 - i) * 0.5;
      pos.setXYZ(v, pos.getX(v) * k, pos.getY(v) * k * 0.7, pos.getZ(v) * k);
    }
    geo.computeVertexNormals();
    const rock = new THREE.Mesh(geo, rockMat);
    const nearPool = i < 6;
    const a = rng.next() * Math.PI * 2;
    const r = nearPool ? pool.radius + 0.8 : 27 + rng.next() * 22;
    const x = nearPool ? pool.x + Math.cos(a) * r : Math.cos(a) * r;
    const z = nearPool ? pool.z + Math.sin(a) * r : Math.sin(a) * r;
    const size = nearPool ? 0.4 + rng.next() * 0.5 : 0.6 + rng.next() * 1.6;
    rock.scale.setScalar(size);
    rock.position.set(x, heightAt(x, z) + size * 0.2, z);
    rock.rotation.y = rng.next() * Math.PI;
    rock.castShadow = rock.receiveShadow = true;
    scene.add(rock);
  }

  // ---------- Trees framing the glade ----------
  const barkMat = new THREE.MeshStandardNodeMaterial({ color: '#4a3526', roughness: 0.95 });
  const leafMat = new THREE.MeshStandardNodeMaterial({ vertexColors: true, roughness: 0.8 });
  const leafGeo = (seed: number) => {
    const geo = new THREE.IcosahedronGeometry(1, 3);
    const pos = geo.attributes.position as THREE.BufferAttribute;
    const col = new Float32Array(pos.count * 3);
    const deep = new THREE.Color('#2c4a1f');
    const warm = new THREE.Color('#a8a23c');
    for (let v = 0; v < pos.count; v++) {
      const x = pos.getX(v);
      const y = pos.getY(v);
      const z = pos.getZ(v);
      const k = 0.8 + noise(x * 1.5 + seed, z * 1.5 + y) * 0.45;
      pos.setXYZ(v, x * k, y * k * 0.8, z * k);
      c.copy(deep).lerp(warm, THREE.MathUtils.clamp(y * 0.5 + 0.3 + (k - 1), 0, 1) * 0.7);
      col.set([c.r, c.g, c.b], v * 3);
    }
    geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    geo.computeVertexNormals();
    return geo;
  };
  const treeCount = quality.tier === 'low' ? 12 : 20;
  for (let i = 0; i < treeCount; i++) {
    const a = (i / treeCount) * Math.PI * 2 + rng.next() * 0.2;
    const r = 38 + rng.next() * 30;
    const x = Math.cos(a) * r;
    const z = Math.sin(a) * r;
    const base = heightAt(x, z);
    const tall = 9 + rng.next() * 8;
    const tree = new THREE.Group();
    const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.45, 0.9, tall, 10), barkMat);
    trunk.position.y = tall / 2;
    trunk.castShadow = true;
    tree.add(trunk);
    const clusters = 4 + rng.int(0, 3);
    for (let k = 0; k < clusters; k++) {
      const leaves = new THREE.Mesh(leafGeo(i * 10 + k), leafMat);
      const s = 3 + rng.next() * 2.5;
      leaves.scale.setScalar(s);
      leaves.position.set(
        (rng.next() - 0.5) * 4,
        tall + (rng.next() - 0.3) * 3,
        (rng.next() - 0.5) * 4,
      );
      leaves.castShadow = true;
      leaves.receiveShadow = true;
      tree.add(leaves);
    }
    tree.position.set(x, base - 0.3, z);
    tree.rotation.y = rng.next() * Math.PI;
    scene.add(tree);
  }

  // ---------- Grass (tall golden grass on the right, meadow beyond the rim) ----------
  const grass = createGrass({
    count: quality.grassBlades,
    extent: 60,
    height: heightAt,
    accept: (x, z) => {
      const r = Math.hypot(x, z);
      if (Math.hypot(x - pool.x, z - pool.z) < pool.radius + 1.2) return false;
      const rightPatch = x > 9 && r < ARENA.floorRadius - 0.5 && z > -10 && z < 8;
      const meadow = r > ARENA.floorRadius + 1 && r < 60;
      return rightPatch || meadow;
    },
  });
  if (grass.count > 0) scene.add(grass);

  // ---------- Fireflies ----------
  const fireflyCount = quality.tier === 'low' ? 60 : 160;
  const fireflyMat = new THREE.MeshBasicNodeMaterial();
  const h1 = hash(instanceIndex);
  const h2 = hash(instanceIndex.add(97));
  const flicker = sin(time.mul(float(2).add(h1.mul(3))).add(h2.mul(40)))
    .mul(0.5)
    .add(0.5);
  fireflyMat.colorNode = color('#fff0a0').mul(flicker.pow(2).mul(5).add(0.3));
  fireflyMat.positionNode = positionLocal.add(
    vec3(
      sin(time.mul(0.35).add(h1.mul(20))).mul(0.8),
      sin(time.mul(0.5).add(h2.mul(30))).mul(0.4),
      cos(time.mul(0.3).add(h2.mul(10))).mul(0.8),
    ),
  );
  const fireflies = new THREE.InstancedMesh(
    new THREE.IcosahedronGeometry(0.035, 0),
    fireflyMat,
    fireflyCount,
  );
  const fm = new THREE.Matrix4();
  for (let i = 0; i < fireflyCount; i++) {
    const a = rng.next() * Math.PI * 2;
    const r = 6 + rng.next() * 30;
    const x = Math.cos(a) * r;
    const z = Math.sin(a) * r;
    fm.makeTranslation(x, heightAt(x, z) + 0.4 + rng.next() * 2.2, z);
    fireflies.setMatrixAt(i, fm);
  }
  fireflies.frustumCulled = false;
  scene.add(fireflies);

  return {
    sun,
    update() {},
  };
}
